import { Link } from 'react-router-dom'
import { useRef, useState } from 'react'
import {
  Calendar,
  User,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  UserCheck,
  Paperclip,
  ExternalLink,
  Upload,
  X,
  FileText,
} from 'lucide-react'
import { toast } from 'sonner'
import { useRequestContext } from './RequestDetailLayout'
import { DatePicker } from '../../components/ui/DatePicker'
import { formatDate } from '../../utils/dateUtils'
import {
  REQUEST_TYPE_LABELS,
  WORK_CENTER_LABELS,
  SHIFT_LABELS,
} from '../../types'

export function RequestOverviewTab() {
  const {
    request,
    otherRequests,
    isEditing,
    isAdmin,
    formData,
    setFormData,
  } = useRequestContext()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [showImageViewer, setShowImageViewer] = useState(false)

  if (!request) return null

  // Determinar si el archivo es una imagen
  const getAttachmentUrl = () => isEditing ? formData.attachmentUrl : request.attachmentUrl
  const getAttachmentName = () => isEditing ? formData.attachmentName : request.attachmentName

  const isImageFile = () => {
    const name = getAttachmentName()?.toLowerCase() || ''
    const url = getAttachmentUrl() || ''
    return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') ||
           url.startsWith('data:image/')
  }

  // Manejar selección de archivo
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Archivo demasiado grande', {
        description: 'El archivo no puede superar los 5MB.',
      })
      return
    }

    // Validar tipo
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido', {
        description: 'Solo se permiten archivos PDF, JPG o PNG.',
      })
      return
    }

    setIsUploadingFile(true)
    try {
      // Convertir a base64
      const reader = new FileReader()
      reader.onloadend = () => {
        setFormData({
          ...formData,
          attachmentUrl: reader.result as string,
          attachmentName: file.name,
        })
        setIsUploadingFile(false)
      }
      reader.onerror = () => {
        toast.error('Error al leer el archivo')
        setIsUploadingFile(false)
      }
      reader.readAsDataURL(file)
    } catch (error) {
      toast.error('Error al procesar el archivo')
      setIsUploadingFile(false)
    }
  }

  // Limpiar archivo
  const handleClearFile = () => {
    setFormData({
      ...formData,
      attachmentUrl: '',
      attachmentName: '',
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Ver documento (imagen en modal, PDF en nueva pestaña)
  const handleViewDocument = () => {
    if (isImageFile()) {
      setShowImageViewer(true)
    } else {
      // Para PDFs, abrir en nueva pestaña
      const url = getAttachmentUrl()
      if (url) {
        window.open(url, '_blank')
      }
    }
  }

  // Descargar documento
  const handleDownloadDocument = () => {
    const url = getAttachmentUrl()
    const name = getAttachmentName()
    if (!url || !name) return

    // Si es base64, crear blob y descargar
    if (url.startsWith('data:')) {
      const link = document.createElement('a')
      link.href = url
      link.download = name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      // Si es URL normal, abrir en nueva pestaña
      window.open(url, '_blank')
    }
  }

  // Determinar si mostrar sección de documento
  const isVacacionesCaseda = request.requestType === 'VACACIONES_FIESTAS_CASEDA'
  const hasAttachment = isEditing
    ? !!(formData.attachmentUrl && formData.attachmentName)
    : !!(request.attachmentUrl && request.attachmentName)
  const showAttachmentSection = isVacacionesCaseda || hasAttachment

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'badge-success'
      case 'DENIED':
        return 'badge-error'
      default:
        return 'badge-warning'
    }
  }

  const getSmallStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="h-3.5 w-3.5" />
      case 'DENIED':
        return <XCircle className="h-3.5 w-3.5" />
      default:
        return <Clock className="h-3.5 w-3.5" />
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Info */}
      <div className="lg:col-span-2 space-y-6">
        {/* Request Info */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Información de la Solicitud
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Tipo de Solicitud
              </label>
              <p className="text-gray-900 dark:text-white">
                {REQUEST_TYPE_LABELS[request.requestType]}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Días Solicitados
              </label>
              <p className="text-2xl font-bold text-primary-600">{request.daysRequested}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Fecha de Inicio
              </label>
              {isEditing ? (
                <DatePicker
                  value={formData.startDate}
                  onChange={(date) => setFormData({ ...formData, startDate: date })}
                  placeholder="DD/MM/AAAA"
                  minDate={isAdmin ? new Date(new Date().getFullYear(), 0, 1) : new Date()}
                  maxDate={new Date(new Date().getFullYear() + 4, 11, 31)}
                />
              ) : (
                <p className="flex items-center gap-2 text-gray-900 dark:text-white">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDate(request.startDate)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                {request.secondDate ? 'Segundo Día' : 'Fecha de Fin'}
              </label>
              {isEditing ? (
                request.secondDate ? (
                  <DatePicker
                    value={formData.secondDate}
                    onChange={(date) => setFormData({ ...formData, secondDate: date })}
                    placeholder="DD/MM/AAAA"
                    minDate={isAdmin ? new Date(new Date().getFullYear(), 0, 1) : new Date()}
                    maxDate={new Date(new Date().getFullYear() + 4, 11, 31)}
                  />
                ) : (
                  <DatePicker
                    value={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    placeholder="DD/MM/AAAA"
                    minDate={formData.startDate ? new Date(formData.startDate) : (isAdmin ? new Date(new Date().getFullYear(), 0, 1) : new Date())}
                    maxDate={new Date(new Date().getFullYear() + 4, 11, 31)}
                  />
                )
              ) : (
                <p className="flex items-center gap-2 text-gray-900 dark:text-white">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {request.secondDate ? formatDate(request.secondDate) : formatDate(request.endDate)}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Notas
              </label>
              {isEditing ? (
                <textarea
                  className="input min-h-[80px]"
                  placeholder="Notas adicionales..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              ) : (
                <p className="text-gray-900 dark:text-white">
                  {request.notes || <span className="text-gray-400 italic">Sin notas</span>}
                </p>
              )}
            </div>

            {/* Documento adjunto */}
            {showAttachmentSection && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  {isVacacionesCaseda ? 'Documento de empadronamiento' : 'Documento adjunto'}
                </label>

                {isEditing ? (
                  <>
                    {/* Input oculto para selección de archivo */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {hasAttachment ? (
                      <div className="flex items-center justify-between p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-success-100 dark:bg-success-800 rounded-lg">
                            <FileText className="h-5 w-5 text-success-600 dark:text-success-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {formData.attachmentName}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleViewDocument}
                            className="btn btn-secondary btn-sm flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            Ver
                          </button>
                          <button
                            type="button"
                            onClick={handleClearFile}
                            className="p-1.5 hover:bg-error-100 dark:hover:bg-error-800 rounded-lg transition-colors"
                            title="Eliminar documento"
                          >
                            <X className="h-4 w-4 text-error-600 dark:text-error-400" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingFile}
                        className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-primary-400 dark:hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors group disabled:opacity-50"
                      >
                        <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                          {isUploadingFile ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
                          ) : (
                            <Upload className="h-6 w-6 group-hover:text-primary-500 transition-colors" />
                          )}
                          <span className="text-sm font-medium group-hover:text-primary-600 dark:group-hover:text-primary-400">
                            {isUploadingFile ? 'Subiendo...' : 'Haz clic para seleccionar archivo'}
                          </span>
                          <span className="text-xs">PDF, JPG o PNG (máx. 5MB)</span>
                        </div>
                      </button>
                    )}
                  </>
                ) : hasAttachment ? (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Paperclip className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {request.attachmentName}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleViewDocument}
                        className="btn btn-secondary btn-sm flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        Ver
                      </button>
                      <button
                        onClick={handleDownloadDocument}
                        className="btn btn-secondary btn-sm flex items-center gap-1"
                        title="Descargar"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500 italic text-sm">
                    Sin documento adjunto
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Denial Reason */}
        {request.status === 'DENIED' && request.denialReason && (
          <div className="card border-l-4 border-error-500">
            <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-error-500" />
              Motivo de Denegación
            </h2>
            <p className="text-gray-700 dark:text-gray-300">{request.denialReason}</p>
          </div>
        )}

        {/* Processed By Info */}
        {request.status !== 'PENDING' && (
          <div className={`card border-l-4 ${request.status === 'APPROVED' ? 'border-success-500' : 'border-error-500'}`}>
            {(() => {
              const isAutomatic = !request.processedByName || request.processedByName === 'Sistema (automático)'
              return (
                <>
                  <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white flex items-center gap-2">
                    <UserCheck className={`h-5 w-5 ${request.status === 'APPROVED' ? 'text-success-500' : 'text-error-500'}`} />
                    {request.status === 'APPROVED' ? 'Aprobada' : 'Denegada'} {isAutomatic ? 'automáticamente' : `por ${request.processedByName}`}
                  </h2>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {isAutomatic
                        ? 'Procesada automáticamente según las reglas del convenio'
                        : 'Procesada manualmente por un administrador'}
                    </p>
                    {request.processedAt && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(request.processedAt)}
                      </p>
                    )}
                  </div>
                </>
              )
            })()}
          </div>
        )}
      </div>

      {/* Sidebar - Employee Info */}
      <div className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <User className="h-5 w-5" />
            Empleado
          </h2>
          <Link
            to={`/employees/${request.employeeId}`}
            className="block hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-lg p-3 -mx-3 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                {request.employeeName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white truncate">
                  {request.employeeName}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {request.employeePosition}
                </p>
              </div>
            </div>
          </Link>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-300">
                {WORK_CENTER_LABELS[request.employeeWorkCenter]}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-300">
                {SHIFT_LABELS[request.employeeShift]}
              </span>
            </div>
          </div>
        </div>

        {/* Otras Solicitudes del Empleado */}
        {otherRequests.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
              Otras Solicitudes
              <span className="badge badge-gray text-xs">{otherRequests.length}</span>
            </h2>
            <div className="space-y-2">
              {otherRequests.slice(0, 5).map((req) => (
                <Link
                  key={req.id}
                  to={`/requests/${req.id}`}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`badge ${getStatusBadgeClass(req.status)} text-xs flex-shrink-0`}>
                      {getSmallStatusIcon(req.status)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                        {REQUEST_TYPE_LABELS[req.requestType].split(' ').slice(0, 2).join(' ')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(req.startDate)}
                      </p>
                    </div>
                  </div>
                  <Eye className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                </Link>
              ))}
              {otherRequests.length > 5 && (
                <Link
                  to={`/employees/${request.employeeId}/requests`}
                  className="block text-center text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 py-1"
                >
                  Ver todas ({otherRequests.length})
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de visualización de imagen */}
      {showImageViewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowImageViewer(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            {/* Botón cerrar */}
            <button
              onClick={() => setShowImageViewer(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2"
            >
              <span className="text-sm">Cerrar</span>
              <X className="h-6 w-6" />
            </button>

            {/* Imagen */}
            <img
              src={getAttachmentUrl() || ''}
              alt={getAttachmentName() || 'Documento adjunto'}
              className="max-w-full max-h-[85vh] mx-auto rounded-lg shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Nombre del archivo y botón descargar */}
            <div className="absolute -bottom-12 left-0 right-0 flex items-center justify-center gap-4">
              <span className="text-white text-sm">{getAttachmentName()}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDownloadDocument()
                }}
                className="text-white hover:text-gray-300 transition-colors flex items-center gap-1 text-sm"
              >
                <ExternalLink className="h-4 w-4" />
                Descargar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
